import type {
  HttpResponse,
  IHttpClient,
  RequestOptions,
  RequestOptionsFactory,
  RequestOverrides,
} from '@wix/http-client';
import { acrKey, StepUpManager } from './StepUpManager';
import { extractStepUpDetails, isStepUpRequiredError } from './stepUpError';
import type { StepUpTokenAttacher } from './types';

export interface StepUpHttpClientOptions {
  // Attaches the token to the request before replay / proactive send. Defaults to
  // `Authorization: Bearer <token>`.
  attachToken?: StepUpTokenAttacher;
}

const defaultAttachToken: StepUpTokenAttacher = (options, token) => ({
  ...options,
  headers: {
    ...(options.headers as Record<string, string> | undefined),
    authorization: `Bearer ${token}`,
  },
});

type RequestInput<T> = RequestOptions<T> | RequestOptionsFactory<T>;

// Wraps an IHttpClient so step-up (RFC 9470) is handled transparently, WITHOUT the
// interceptor API — a plain decorator over any published `@wix/http-client`. Use this
// today; switch to {@link enableStepUpAuth} once metro #8327's interceptor API ships.
// Never stack both on one client.
//
// Every verb funnels through `request` → `run`, the single interception point. This
// class owns ONLY the HTTP concern (intercept STEP_UP_REQUIRED → get token → attach →
// replay); the token lifecycle (per-acr cache, mint flow, single-flight) lives in the
// shared {@link StepUpManager}, so the same cache serves the client-driven trigger.
//
// Per-request escalation: proactive attach for a known endpoint, then evict-then-mint
// — a cached token the server rejects is evicted (never reused again), then a fresh
// step-up runs, looping on every STEP_UP_REQUIRED until the server accepts, a
// non-step-up error is thrown, or the step-up flow fails/dismisses. No cap.
//
// Typed against IHttpClient (structural) so it composes with http-client v1 and v2;
// consumers on v1 cast at the seam (`as unknown as HttpClient`).
export class StepUpHttpClient implements IHttpClient {
  private readonly inner: IHttpClient;
  private readonly manager: StepUpManager;
  private readonly attachToken: StepUpTokenAttacher;

  constructor(
    inner: IHttpClient,
    manager: StepUpManager,
    options: StepUpHttpClientOptions = {},
  ) {
    this.inner = inner;
    this.manager = manager;
    this.attachToken = options.attachToken ?? defaultAttachToken;
  }

  request<T = any>(
    requestOptionsOrFactory: RequestInput<T>,
    overrides?: RequestOverrides,
  ): Promise<HttpResponse<T>> {
    return this.run(requestOptionsOrFactory, overrides);
  }

  get<T = any>(url: string, requestOptions?: RequestOptions<T>) {
    return this.request<T>({ ...requestOptions, url, method: 'GET' });
  }

  delete<T = any>(url: string, requestOptions?: RequestOptions<T>) {
    return this.request<T>({ ...requestOptions, url, method: 'DELETE' });
  }

  head<T = any>(url: string, requestOptions?: RequestOptions<T>) {
    return this.request<T>({ ...requestOptions, url, method: 'HEAD' });
  }

  options<T = any>(url: string, requestOptions?: RequestOptions<T>) {
    return this.request<T>({ ...requestOptions, url, method: 'OPTIONS' });
  }

  post<T = any>(url: string, data?: any, requestOptions?: RequestOptions<T>) {
    return this.request<T>({ ...requestOptions, url, data, method: 'POST' });
  }

  put<T = any>(url: string, data?: any, requestOptions?: RequestOptions<T>) {
    return this.request<T>({ ...requestOptions, url, data, method: 'PUT' });
  }

  patch<T = any>(url: string, data?: any, requestOptions?: RequestOptions<T>) {
    return this.request<T>({ ...requestOptions, url, data, method: 'PATCH' });
  }

  get CancelToken() {
    return this.inner.CancelToken;
  }

  get isCancel() {
    return this.inner.isCancel;
  }

  private async run<T>(
    input: RequestInput<T>,
    overrides: RequestOverrides | undefined,
  ): Promise<HttpResponse<T>> {
    const endpointKey = this.endpointKey(input);
    // Proactively attach the token cached for this endpoint's known acr, so a repeat
    // call skips the first challenge entirely.
    const knownAcr = endpointKey
      ? this.manager.knownAcrForEndpoint(endpointKey)
      : undefined;
    // Explicit undefined check, not truthiness: the max_age-only acr key is '' and
    // must still resolve its cached token.
    let token: string | null =
      knownAcr !== undefined ? this.manager.cachedTokenForAcr(knownAcr) : null;

    for (;;) {
      try {
        return await this.inner.request(
          token ? this.withToken(input, token) : input,
          overrides,
        );
      } catch (error) {
        if (!isStepUpRequiredError(error)) {
          throw error;
        }
        const details = extractStepUpDetails(error);
        const acr = acrKey(details.acrValues);
        if (endpointKey) {
          this.manager.rememberEndpointAcr(endpointKey, acr);
        }

        // The token we just sent was rejected — evict it so it is never reused again.
        if (token) {
          this.manager.evictToken(acr, token);
        }

        // Reuse a not-yet-sent cached token for this acr (minted meanwhile, e.g. by a
        // concurrent flow); otherwise mint a fresh one. Keep stepping up on every
        // STEP_UP_REQUIRED — the loop ends on success, a non-step-up error, or the
        // step-up flow failing/dismissing (which stepUp() rejects with).
        const cached = this.manager.cachedTokenForAcr(acr);
        if (cached && cached !== token) {
          token = cached;
          continue;
        }
        token = await this.manager.stepUp(acr, details);
      }
    }
  }

  // Stable identifier for the request ("METHOD url"). Best-effort: a factory is
  // resolved with a neutral context purely to read url/method, and any failure just
  // disables the per-endpoint optimisation (the reactive flow still works).
  private endpointKey<T>(input: RequestInput<T>): string | null {
    try {
      const options =
        typeof input === 'function'
          ? (input as RequestOptionsFactory<T>)({ isSSR: false, host: '' })
          : input;
      if (!options?.url) {
        return null;
      }
      return `${(options.method ?? 'GET').toUpperCase()} ${options.url}`;
    } catch {
      return null;
    }
  }

  private withToken<T>(input: RequestInput<T>, token: string): RequestInput<T> {
    if (typeof input === 'function') {
      const factory = input as RequestOptionsFactory<T>;
      return (context) =>
        this.attachToken(factory(context), token) as RequestOptions<T>;
    }
    return this.attachToken(input, token) as RequestOptions<T>;
  }
}

// Convenience factory: wrap `httpClient` so step-up 403s are handled transparently,
// sharing `manager`'s token cache. Drop-in — the result is still an IHttpClient.
export const withStepUp = (
  httpClient: IHttpClient,
  manager: StepUpManager,
  options?: StepUpHttpClientOptions,
): IHttpClient => new StepUpHttpClient(httpClient, manager, options);
